import React, { useState, useEffect, useRef, useContext } from "react";
import { FaChevronLeft, FaChevronRight, FaShoppingCart, FaBolt } from "react-icons/fa";
import axios from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

  .ps-container {
    font-family: 'Inter', sans-serif;
    padding: 70px 0;
    background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #f5f3ff 100%);
    position: relative;
    overflow: hidden;
    border-radius: 48px;
  }

  .ps-container::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  .ps-container::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -20%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(139,92,246,0.04) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  .ps-header {
    text-align: center;
    margin-bottom: 60px;
    position: relative;
    z-index: 2;
  }

  .ps-badge {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    font-size: 13px;
    font-weight: 700;
    padding: 8px 24px;
    border-radius: 100px;
    margin-bottom: 24px;
    letter-spacing: 0.5px;
    box-shadow: 0 6px 20px rgba(99,102,241,0.3);
    animation: ps-fadeInDown 0.6s ease-out;
  }

  .ps-title {
    font-size: clamp(36px, 5vw, 52px);
    font-weight: 800;
    background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 0 16px;
    letter-spacing: -0.02em;
    animation: ps-fadeInUp 0.6s ease-out 0.1s both;
  }

  .ps-subtitle {
    font-size: clamp(16px, 2.5vw, 18px);
    color: #64748b;
    font-weight: 400;
    max-width: 650px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    animation: ps-fadeInUp 0.6s ease-out 0.2s both;
  }

  /* Loading State */
  .ps-loading {
    width: 100%;
    padding: 80px 0;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  }

  .ps-loading-content {
    text-align: center;
  }

  .ps-spinner-wrap {
    display: inline-block;
    position: relative;
  }

  .ps-spinner {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 4px solid #c7d2fe;
    border-top-color: #4f46e5;
    animation: ps-spin 1s linear infinite;
  }

  .ps-spinner-icon {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6366f1;
    font-size: 24px;
    animation: ps-pulse 1.5s ease-in-out infinite;
  }

  @keyframes ps-spin {
    to { transform: rotate(360deg); }
  }

  @keyframes ps-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .ps-loading-text {
    margin-top: 24px;
    color: #6b7280;
    font-weight: 500;
  }

  /* Empty State */
  .ps-empty {
    width: 100%;
    padding: 80px 0;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  }

  .ps-empty-content {
    text-align: center;
  }

  .ps-empty-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 112px;
    height: 112px;
    background: linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%);
    border-radius: 50%;
    margin-bottom: 24px;
  }

  .ps-empty-icon {
    font-size: 48px;
    color: #6366f1;
  }

  .ps-empty-title {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }

  .ps-empty-text {
    color: #9ca3af;
    margin-top: 8px;
  }

  /* Scroll Wrapper */
  .ps-scroll-wrapper {
    position: relative;
    max-width: 80rem;
    margin: 0 auto;
    padding: 0 16px;
  }

  @media (min-width: 768px) {
    .ps-scroll-wrapper {
      padding: 0 32px;
    }
  }

  /* Scroll Container */
  .ps-scroll-container {
    display: flex;
    gap: 24px;
    overflow-x: auto;
    padding-bottom: 24px;
    padding-left: 8px;
    padding-right: 8px;
    scrollbar-width: thin;
    scrollbar-color: #6366f1 #c7d2fe;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
  }

  .ps-scroll-container::-webkit-scrollbar {
    height: 6px;
  }

  .ps-scroll-container::-webkit-scrollbar-track {
    background: #c7d2fe;
    border-radius: 10px;
  }

  .ps-scroll-container::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-radius: 10px;
  }

  /* Product Card */
  .ps-card {
    background: white;
    border-radius: 28px;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    box-shadow: 0 10px 30px -8px rgba(0,0,0,0.08);
    position: relative;
    cursor: pointer;
    flex-shrink: 0;
    width: 290px;
    animation: ps-fadeInUp 0.5s ease-out forwards;
    opacity: 0;
  }

  @media (min-width: 768px) {
    .ps-card {
      width: 310px;
    }
  }

  @media (max-width: 640px) {
    .ps-card {
      width: 240px;
    }
  }

  .ps-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 30px 50px -15px rgba(99,102,241,0.25);
  }

  .ps-card::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 28px;
    padding: 2px;
    background: linear-gradient(135deg, transparent 0%, #6366f1 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  .ps-card:hover::after {
    opacity: 1;
  }

  .ps-card:nth-child(1) { animation-delay: 0.05s; }
  .ps-card:nth-child(2) { animation-delay: 0.1s; }
  .ps-card:nth-child(3) { animation-delay: 0.15s; }
  .ps-card:nth-child(4) { animation-delay: 0.2s; }
  .ps-card:nth-child(5) { animation-delay: 0.25s; }
  .ps-card:nth-child(6) { animation-delay: 0.3s; }
  .ps-card:nth-child(7) { animation-delay: 0.35s; }
  .ps-card:nth-child(8) { animation-delay: 0.4s; }

  /* Product Image */
  .ps-image-wrapper {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
    border-radius: 24px;
    margin: 12px;
    aspect-ratio: 1;
  }

  .ps-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .ps-card:hover .ps-image {
    transform: scale(1.1);
  }

  /* Badges */
  .ps-badge-low {
    position: absolute;
    top: 12px;
    left: 12px;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    padding: 5px 14px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 700;
    z-index: 3;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }

  .ps-badge-new {
    position: absolute;
    top: 12px;
    right: 12px;
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    color: white;
    padding: 5px 14px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 700;
    z-index: 3;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }

  .ps-badge-trending {
    position: absolute;
    top: 12px;
    left: 12px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    padding: 5px 14px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 700;
    z-index: 3;
  }

  /* Wishlist */
  .ps-wishlist-btn {
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(255,255,255,0.95);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
    z-index: 3;
    backdrop-filter: blur(4px);
  }

  .ps-wishlist-btn:hover {
    background: #fef2f2;
    transform: scale(1.15);
  }

  /* Quick View */
  .ps-quick-view-overlay {
    position: absolute;
    inset: 0;
    background: rgba(99,102,241,0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 24px;
    z-index: 2;
  }

  .ps-image-wrapper:hover .ps-quick-view-overlay {
    opacity: 1;
  }

  .ps-quick-view-btn {
    background: white;
    border: none;
    padding: 10px 20px;
    border-radius: 100px;
    font-weight: 600;
    font-size: 13px;
    color: #6366f1;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Inter', sans-serif;
  }

  .ps-quick-view-btn:hover {
    background: #6366f1;
    color: white;
    transform: scale(1.05);
  }

  /* Card Body */
  .ps-card-body {
    padding: 20px;
  }

  .ps-card-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .ps-category {
    display: inline-block;
    background: #f1f5f9;
    color: #475569;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: 100px;
    letter-spacing: 0.3px;
    transition: all 0.2s ease;
  }

  .ps-card:hover .ps-category {
    background: #e0e7ff;
    color: #6366f1;
  }

  .ps-commission-pill {
    background: #e0e7ff;
    border-radius: 100px;
    padding: 5px 12px;
    font-size: 11px;
    font-weight: 700;
    color: #6366f1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .ps-product-name {
    font-weight: 700;
    color: #1f2937;
    font-size: 16px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 50px;
    transition: color 0.2s;
    margin: 0 0 12px;
  }

  .ps-product-name:hover {
    color: #4f46e5;
  }

  .ps-price-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
    font-size: 14px;
    font-weight: 600;
    color: #000;
  }

  .ps-price-row span {
    font-size: 14px;
    font-weight: 600;
    color: #000;
  }

  /* Cart Controls */
  .ps-cart-controls {
    margin-top: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ps-qty-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ps-qty-btn {
    width: 32px;
    height: 32px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    font-size: 16px;
    font-family: 'Inter', sans-serif;
  }

  .ps-qty-btn:hover {
    background: #f9fafb;
  }

  .ps-qty-value {
    width: 32px;
    text-align: center;
    font-weight: 700;
  }

  .ps-add-btn {
    font-size: 12px;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 100px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Inter', sans-serif;
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: white;
  }

  .ps-add-btn:hover:not(:disabled) {
    box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.4);
    transform: scale(1.05);
    background: linear-gradient(135deg, #4338ca, #6d28d9);
  }

  .ps-add-btn:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }

  .ps-add-btn svg {
    font-size: 11px;
  }

  /* Scroll Buttons */
  .ps-scroll-btn {
    position: absolute;
    top: 45%;
    transform: translateY(-50%);
    background: white;
    border: none;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 12px 30px -8px rgba(0,0,0,0.15);
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 20;
    color: #6366f1;
  }

  .ps-scroll-btn:hover {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    transform: translateY(-50%) scale(1.12);
    box-shadow: 0 20px 35px -10px rgba(99,102,241,0.4);
  }

  .ps-scroll-btn:active {
    transform: translateY(-50%) scale(0.96);
  }

  .ps-scroll-btn-left {
    left: 10px;
  }

  .ps-scroll-btn-right {
    right: 10px;
  }

  @media (min-width: 768px) {
    .ps-scroll-btn-left {
      left: -20px;
    }
    .ps-scroll-btn-right {
      right: -20px;
    }
  }

  @media (min-width: 1024px) {
    .ps-scroll-btn-left {
      left: -30px;
    }
    .ps-scroll-btn-right {
      right: -30px;
    }
  }

  /* Auto-play Indicator */
  .ps-indicator {
    display: flex;
    justify-content: center;
    margin-top: 32px;
    gap: 8px;
  }

  .ps-indicator-bar {
    height: 6px;
    border-radius: 9999px;
    transition: all 0.3s ease;
  }

  .ps-indicator-active {
    width: 56px;
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
  }

  .ps-indicator-inactive {
    width: 12px;
    background: #d1d5db;
  }

  .ps-autoplay-status {
    text-align: center;
    font-size: 12px;
    color: #9ca3af;
    margin-top: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .ps-status-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .ps-status-dot-active {
    background: #22c55e;
    animation: ps-pulse 1.5s ease-in-out infinite;
  }

  .ps-status-dot-inactive {
    background: #9ca3af;
  }

  /* Animations */
  @keyframes ps-fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ps-fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Responsive */
  @media (max-width: 768px) {
    .ps-container {
      padding: 40px 0;
    }
    .ps-header {
      margin-bottom: 32px;
    }
    .ps-scroll-btn {
      width: 40px;
      height: 40px;
    }
    .ps-scroll-btn svg {
      width: 16px;
      height: 16px;
    }
    .ps-price-row span {
      font-size: 12px;
    }
  }

  @media (max-width: 640px) {
    .ps-badge {
      padding: 6px 18px;
      font-size: 11px;
    }
    .ps-card-body {
      padding: 16px;
    }
    .ps-product-name {
      font-size: 14px;
      min-height: 44px;
    }
  }
`;

const ProductSlider = () => {
  const { user } = useContext(AuthContext);
  const { addToCart, removeFromCart, updateQuantity, isInCart, getItemQuantity } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);
  const scrollContainerRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    return () => {
      if (autoPlayIntervalRef.current) clearInterval(autoPlayIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (autoPlay && products.length > 0) {
      autoPlayIntervalRef.current = setInterval(() => {
        scrollNext();
      }, 3000);
    }
    return () => {
      if (autoPlayIntervalRef.current) clearInterval(autoPlayIntervalRef.current);
    };
  }, [autoPlay, products]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get("/product/available");
      setProducts(res.data.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -340, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 340, behavior: "smooth" });
    }
  };

  const scrollNext = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.children[0]?.offsetWidth || 300;
      container.scrollBy({ left: cardWidth + 24, behavior: "smooth" });

      if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 150) {
        setTimeout(() => {
          container.scrollTo({ left: 0, behavior: "smooth" });
        }, 200);
      }
    }
  };

  const handleMouseEnter = () => setAutoPlay(false);
  const handleMouseLeave = () => setAutoPlay(true);

  if (loading) {
    return (
      <div className="ps-loading">
        <style>{styles}</style>
        <div className="ps-loading-content">
          <div className="ps-spinner-wrap">
            <div className="ps-spinner"></div>
            <div className="ps-spinner-icon">
              <FaBolt />
            </div>
          </div>
          <p className="ps-loading-text">Loading amazing products...</p>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="ps-empty">
        <style>{styles}</style>
        <div className="ps-empty-content">
          <div className="ps-empty-icon-wrap">
            <FaShoppingCart className="ps-empty-icon" />
          </div>
          <h3 className="ps-empty-title">No products available</h3>
          <p className="ps-empty-text">Check back later for amazing products!</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>

      <div className="ps-container">
        <div className="ps-header">
          <span className="ps-badge">
            <FaBolt className="ps-badge-icon" />
            Limited Time Offer
          </span>
          <h2 className="ps-title">Featured Products</h2>
          <p className="ps-subtitle">
            Discover our handpicked collection of premium products with exclusive discounts
          </p>
        </div>

        <div
          className="ps-scroll-wrapper"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button onClick={scrollLeft} className="ps-scroll-btn ps-scroll-btn-left" aria-label="Scroll left">
            <FaChevronLeft size={22} />
          </button>

          <button onClick={scrollRight} className="ps-scroll-btn ps-scroll-btn-right" aria-label="Scroll right">
            <FaChevronRight size={22} />
          </button>

          <div
            ref={scrollContainerRef}
            className="ps-scroll-container"
          >
            {products.map((product, index) => {
              const inCart = isInCart(product._id);
              const cartQuantity = getItemQuantity(product._id);

              return (
                <div
                  key={product._id}
                  className="ps-card"
                >
                  <div className="ps-image-wrapper">
                    <img
                      src={product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop"}
                      alt={product.name}
                      className="ps-image"
                      loading="lazy"
                    />
                  </div>

                  <div className="ps-card-body">
                    <h3 className="ps-product-name">
                      {product.name}
                    </h3>

                    <div className="ps-price-row">
                      <span>MRP: ₹{(product?.mrp || 0).toLocaleString()}</span>
                      {user && (
                        <span>SP: ₹{(product?.sp || 0).toLocaleString()}</span>
                      )}
                      {user && (
                        <span>PV: ₹{(product?.pv || 0).toLocaleString()}</span>
                      )}
                    </div>

                    <div className="ps-cart-controls">
                      {inCart ? (
                        <div className="ps-qty-group">
                          <button
                            className="ps-qty-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(product._id, cartQuantity - 1);
                            }}
                          >
                            −
                          </button>
                          <span className="ps-qty-value">{cartQuantity}</span>
                          <button
                            className="ps-qty-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              addToCart(product._id, product.name);
                            }}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          className="ps-add-btn"
                          disabled={product.quantity === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product._id, product.name);
                          }}
                        >
                          <FaShoppingCart />
                          Add to Cart
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ps-indicator">
            <div className={`ps-indicator-bar ${autoPlay ? "ps-indicator-active" : "ps-indicator-inactive"}`} />
            <div className={`ps-indicator-bar ${!autoPlay ? "ps-indicator-active" : "ps-indicator-inactive"}`} />
          </div>

          <p className="ps-autoplay-status">
            <span className={`ps-status-dot ${autoPlay ? "ps-status-dot-active" : "ps-status-dot-inactive"}`}></span>
            {autoPlay ? "Auto-scrolling • Hover to pause" : "Paused • Move mouse to resume"}
          </p>
        </div>
      </div>
    </>
  );
};

export default ProductSlider;